import React, { createContext, useCallback, useState } from 'react'
import kebabCase from 'lodash/kebabCase'
import { Toast, toastTypes } from 'components/Toast'
import { reportClientError } from 'utils/reportClientError'
import { chainReadErrorMessage, isQuotaExhausted } from 'utils/chainReadErrors'
import { ToastContextApi } from './types'

export const ToastsContext = createContext<ToastContextApi>(undefined)

/** The app's ways of saying "we do not know what happened". */
const GENERIC_FAILURE_PHRASES = ['Something went wrong', 'Unknown error']

/** A toast that reports the user's own decision is not worth recording. */
const USER_DECISION_PHRASES = ['rejected', 'denied', 'cancelled', 'canceled']

type ToastsProviderProps = {
  children?: React.ReactNode
}

export const ToastsProvider: React.FC<ToastsProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastContextApi['toasts']>([])

  const toast = useCallback(
    ({ title, description, type, buttonText, onButtonClick }: Omit<Toast, 'id'>) => {
      setToasts((prevToasts) => {
        const id = kebabCase(title)

        // Remove any existing toasts with the same id
        const currentToasts = prevToasts.filter((prevToast) => prevToast.id !== id)

        return [
          {
            id,
            title,
            description,
            type,
            buttonText,
            onButtonClick
          },
          ...currentToasts
        ]
      })
    },
    [setToasts]
  )

  const toastError = useCallback(
    (
      title: Toast['title'],
      description?: Toast['description'],
      buttonText?: Toast['buttonText'],
      onButtonClick?: Toast['onButtonClick'],
      ttl?: Toast['ttl']
    ) => {
      /*
       * Every error toast is recorded, without the 72 call sites knowing.
       *
       * The rule this enforces: the dapp must never tell somebody something
       * failed while we have no idea it happened. People do not write reports
       * — they screenshot the toast and send it, and until now that screenshot
       * was the only copy of the information. Anything routed through here is
       * an error the app CHOSE to show a person, which makes it exactly the
       * set worth having.
       *
       * Putting it at the choke point rather than at each `toastError` call is
       * deliberate: a convention applied in 72 places is a convention that
       * decays, and the next error toast somebody writes is covered for free.
       */
      /*
       * A provider refusing to answer must never reach a person as a wall of
       * ethers text.
       *
       * The reported case: an RPC node out of quota answered HTTP 200 with a
       * JSON-RPC error, ethers rendered it as "Transaction reverted without a
       * reason string", and the whole dump — including the provider's pricing
       * URL — went on screen. The user was told their transaction had
       * reverted. Nothing had.
       *
       * Rewritten here, at the one place every error toast passes through, for
       * the same reason the reporting sits here: a rule applied at 72 call
       * sites is a rule that decays. The original text still goes to
       * observability below, so nothing is lost for us — only for the person,
       * who could not have used it.
       */
      const raw = [title, description].filter(Boolean).join(' — ')
      const friendly = chainReadErrorMessage(raw)
      if (friendly) {
        // eslint-disable-next-line no-param-reassign
        title = isQuotaExhausted(raw)
          ? "Today's node allowance is used up"
          : 'The network is busy'
        // eslint-disable-next-line no-param-reassign
        description = friendly
      }

      const message = raw.slice(0, 500)
      const isUserDecision = USER_DECISION_PHRASES.some((p) => message.toLowerCase().includes(p))
      if (!isUserDecision)
        reportClientError({
          kind: 'toast',
          message,
          /*
           * `handled: false` marks the toasts that said nothing useful, and
           * those are the ones to hunt. Marking every toast as handled would
           * have made the field meaningless in exactly the case it exists for:
           * "Something went wrong, try again in a while" is the app admitting it
           * had no idea, and it must not be recorded as if it did.
           *
           * Matched on the English text because English is the only locale the
           * app ships. Adding a second one means giving the generic messages a
           * marker instead of a phrase.
           */
          handled: !GENERIC_FAILURE_PHRASES.some((phrase) => message.includes(phrase))
        })
      return toast({ title, description, type: toastTypes.DANGER, buttonText, onButtonClick, ttl })
    },
    [toast]
  )

  const toastInfo = useCallback(
    (title: Toast['title'], description?: Toast['description']) => {
      return toast({ title, description, type: toastTypes.INFO })
    },
    [toast]
  )

  const toastSuccess = useCallback(
    (title: Toast['title'], description?: Toast['description']) => {
      return toast({ title, description, type: toastTypes.SUCCESS })
    },
    [toast]
  )

  const toastWarning = useCallback(
    (title: Toast['title'], description?: Toast['description']) => {
      return toast({ title, description, type: toastTypes.WARNING })
    },
    [toast]
  )

  const clear = useCallback(() => setToasts([]), [])
  const remove = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((prevToast) => prevToast.id !== id))
  }, [])

  return (
    <ToastsContext.Provider value={{ toasts, clear, remove, toastError, toastInfo, toastSuccess, toastWarning }}>
      {children}
    </ToastsContext.Provider>
  )
}
